/**
 * The /workflow/:workflowId route.
 *
 * With the operator chat flag on, the workflow becomes the anchor surface in a
 * chat workspace: the canvas fills the right pane and the rail sits beside it.
 * Artifacts and the artifact gallery are layerable over the workflow via search
 * params; closing them returns to the workflow. The rail starts collapsed —
 * arriving at a workflow means you came to build, and chat is an offer.
 *
 * With the flag off (EY production today), the page renders the bare
 * WorkflowEditorPage with its NodeExplorerSidebar and legacy chat tab,
 * identical to the current behaviour.
 *
 * /workflow/new always uses the legacy path: the fluid host has no way to
 * follow the id the editor mints on first save, and no id to show in the
 * surface chip.
 */

import { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useParams } from 'react-router';

import {
  AgentChatProvider,
  buildArtifactSurfaceContext,
  type SurfaceContext,
} from '@/components/agent-chat';
import { ChatWorkspace } from '@/components/chat-workspace/chat-workspace';
import { useSurfaceChatHost } from '@/components/chat-workspace/use-surface-chat-host';
import { useWorkspaceSurface } from '@/components/chat-workspace/workspace-url';
import type { SurfaceDescriptor } from '@/components/surface-host/surface-host';
import { ROUTES } from '@/constants/routes';
import { usePrimaryAgent } from '@/hooks/queries/agents/use-primary-agent';
import { useGetArtifact } from '@/hooks/queries/artifacts/use-get-artifact';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useWorkflowCanvasSurfaceContext } from '@/hooks/use-workflow-canvas-surface-context';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { dispatchCanvasRun } from '@/stores/canvas-run-bridge-store';
import { dispatchCanvasApplyMutations } from '@/stores/canvas-apply-mutations-bridge-store';
import type { Agent } from '@/types/agent-types';
import type { ArtifactListItem } from '@/types/artifacts';

import { WorkflowEditorPage } from './workflow-editor-page';

export function WorkflowEditor() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { isCanvasA2aChatEnabled } = useLaunchDarkly();
  const { data: primaryAgent, isPending: isPrimaryAgentPending } =
    usePrimaryAgent();

  if (isCanvasA2aChatEnabled && workflowId && workflowId !== 'new') {
    // The primary agent is what decides whether the fluid workspace or the
    // legacy editor renders — falling through to the legacy editor while it
    // loads would mount it only to tear it down and remount as the fluid
    // workspace once the agent arrives, flashing the old layout.
    if (isPrimaryAgentPending) {
      return (
        <div className='flex h-full w-full items-center justify-center'>
          <Loader2 className='size-6 animate-spin text-muted-foreground' />
        </div>
      );
    }
    if (primaryAgent) {
      return (
        <WorkflowChatWorkspace agent={primaryAgent} workflowId={workflowId} />
      );
    }
  }

  return <WorkflowEditorPage />;
}

function WorkflowChatWorkspace({
  agent,
  workflowId,
}: {
  agent: Agent;
  workflowId: string;
}) {
  const navigate = useOrgNavigate();
  // Surface + chat-open state live in the URL. Chat defaults to collapsed:
  // arriving here means you came to build, and chat is an offer.
  const {
    surface: urlSurface,
    chatOpen,
    runId: urlRunId,
    setSurface,
    setChatOpen,
    setRunId,
  } = useWorkspaceSurface({ chatOpenByDefault: false });

  // The workflow is this route's anchor. Resolve the conversation to resume
  // using the surface-level linkage: most recent thread that lists this
  // workflow_id, which includes threads started while viewing an artifact of
  // the workflow.
  const workflowSurface = useMemo(
    () => ({ kind: 'workflow' as const, workflowId }),
    [workflowId],
  );
  const {
    conversationId,
    conversationLinkResolving,
    onConversationChange,
    onTurnComplete,
    artifactLineage: {
      absorb: absorbArtifacts,
      keyFor: artifactContextKey,
      meta: artifactMeta,
    },
    closeLayeredSurface,
    renderRailHeader,
    isMountedCanvasLocked,
    applyMutationsDisabled,
    applyMutationsDisabledReason,
  } = useSurfaceChatHost({
    agentId: agent.id,
    resumeSurface: workflowSurface,
    setSurface,
  });

  // The anchor surface is the workflow itself. A layered ?artifact=,
  // ?gallery=, or foreign ?workflow= wins over it for grounding and display.
  const layeredSurface =
    urlSurface?.kind === 'artifact' ||
    urlSurface?.kind === 'artifact_gallery' ||
    (urlSurface?.kind === 'workflow' && urlSurface.workflowId !== workflowId)
      ? urlSurface
      : null;

  // The anchor surface carries the URL's runId when no layer is showing.
  const anchorSurface: SurfaceDescriptor = useMemo(
    () => ({
      kind: 'workflow',
      workflowId,
      runId: urlRunId ?? undefined,
    }),
    [workflowId, urlRunId],
  );

  // Whether the workflow canvas pane is visible. Starts true; the user can
  // dismiss it with the X when the chat rail is open, collapsing to full-chat
  // while staying on the same page. Re-shown whenever any surface is opened
  // from chat (run card, artifact frame, gallery card).
  const [showAnchorCanvas, setShowAnchorCanvas] = useState(true);

  // The surface passed to the workspace: layered > anchor > hidden (full-chat).
  const surface: SurfaceDescriptor | null =
    layeredSurface ?? (showAnchorCanvas ? anchorSurface : null);

  // Unlike a layered foreign workflow (a peek, safe to abandon), the anchor
  // is this route's own workflow — editing it here should warn before the
  // user navigates away with unsaved changes, same as the standalone editor.
  const isAnchorSurfaceVisible = surface === anchorSurface;

  // Open a workflow. When it's the same workflow that anchors this route,
  // just update the run param — no route change, so the conversation and any
  // in-flight turn survive. Otherwise layer the foreign workflow.
  const openWorkflow = useCallback(
    (id: string, runId?: string) => {
      setShowAnchorCanvas(true);
      if (id === workflowId) {
        // Clear any layer first: setSurface(null) drops ?runId= when a
        // ?workflow= layer was showing (it belonged to that layer), so
        // clearing it before writing the anchor's own run keeps the two
        // from fighting over the one query param.
        if (layeredSurface) setSurface(null);
        setRunId(runId ?? null);
      } else {
        setSurface({ kind: 'workflow', workflowId: id, runId });
      }
    },
    [workflowId, layeredSurface, setRunId, setSurface],
  );

  // Open an artifact in the surface pane.
  const openArtifact = useCallback(
    (artifactId: string) => {
      setShowAnchorCanvas(true);
      setSurface({ kind: 'artifact', artifactId });
    },
    [setSurface],
  );

  // Open an artifact from the gallery, carrying user_role.
  const openArtifactFromGallery = useCallback(
    (artifact: ArtifactListItem) => {
      setShowAnchorCanvas(true);
      setSurface({
        kind: 'artifact',
        artifactId: artifact.id,
        userRole: artifact.user_role,
      });
    },
    [setSurface],
  );

  const openWorkflowArtifacts = useCallback(
    (wfId: string) => {
      setSurface({ kind: 'artifact_gallery', workflowId: wfId, locked: true });
    },
    [setSurface],
  );

  // Ground the agent in whatever is actually showing.
  // Workflow anchor (and layered foreign workflow): live graph.
  // Keep grounding on the anchor workflow even when its pane is dismissed —
  // the user is still on this workflow page and the agent should know that.
  const groundedWorkflowId =
    surface?.kind === 'workflow'
      ? surface.workflowId
      : !layeredSurface
        ? workflowId // anchor dismissed but still the context
        : null;
  const workflowContext = useWorkflowCanvasSurfaceContext(
    groundedWorkflowId,
    isMountedCanvasLocked,
  );

  // Layered artifact: resolve its detail for the context key.
  const layeredArtifactId =
    layeredSurface?.kind === 'artifact' ? layeredSurface.artifactId : null;
  const { data: layeredArtifact, isPending: layeredArtifactPending } =
    useGetArtifact(layeredArtifactId);

  const surfaceContext = useMemo((): SurfaceContext | null => {
    if (!surface) {
      // Canvas dismissed — keep grounding on the anchor workflow so the agent
      // knows what page the user is on, even with the pane hidden.
      return workflowContext;
    }
    if (surface.kind === 'workflow') {
      return workflowContext;
    }
    if (surface.kind === 'artifact') {
      return buildArtifactSurfaceContext({
        artifactId: surface.artifactId,
        artifactContextKey,
        meta: artifactMeta.get(surface.artifactId),
        artifact: layeredArtifact,
      });
    }
    if (surface.kind === 'artifact_gallery') {
      return {
        surface: 'artifact_gallery',
        workflow_id: surface.workflowId,
      };
    }
    return null;
  }, [
    surface,
    workflowContext,
    artifactContextKey,
    artifactMeta,
    layeredArtifact,
  ]);

  // Wait on the artifact fetch before writing its context marker, so the
  // marker names the artifact rather than reading as "an artifact" forever.
  const surfaceResolving =
    surface?.kind === 'artifact' && layeredArtifactPending;

  // onRunChange from the embedded canvas: keep ?runId= in the URL so a reload
  // returns to the same run.
  const handleRunChange = useCallback(
    (_wfId: string, runId: string | null) => {
      setRunId(runId);
    },
    [setRunId],
  );

  return (
    <AgentChatProvider
      agentId={agent.id}
      conversationId={conversationId}
      conversationLinkResolving={conversationLinkResolving}
      onConversationChange={onConversationChange}
      onTurnComplete={onTurnComplete}
      surfaceContext={surfaceContext}
      openWorkflowId={groundedWorkflowId}
      // Grounding outlives the pane, but the chat must not defer to a canvas
      // that is no longer on screen: the workflow card's click is what
      // brings the dismissed pane back.
      canvasVisible={surface?.kind === 'workflow'}
      surfaceResolving={surfaceResolving}
      onOpenWorkflow={openWorkflow}
      onArtifact={(artifact) => {
        absorbArtifacts([artifact]);
        if (artifact.artifact_id) openArtifact(artifact.artifact_id);
      }}
      onArtifactHistory={absorbArtifacts}
      onOpenArtifact={openArtifact}
      onRunWorkflow={dispatchCanvasRun}
      onApplyMutations={dispatchCanvasApplyMutations}
      applyMutationsDisabled={applyMutationsDisabled}
      applyMutationsDisabledReason={applyMutationsDisabledReason}
    >
      <ChatWorkspace
        agentId={agent.id}
        surface={surface}
        // Layered surface: X returns to the workflow anchor.
        // Anchor workflow: X dismisses the canvas pane so chat can go
        // full-width. Only offered when the chat rail is visible — with no
        // rail on screen "close" has nowhere to take the user.
        onCloseSurface={
          layeredSurface
            ? closeLayeredSurface
            : chatOpen
              ? () => setShowAnchorCanvas(false)
              : undefined
        }
        // Deleting a layered peek returns to this route's own workflow;
        // deleting the anchor kills the route itself, so it leaves for the list.
        onWorkflowDeleted={
          layeredSurface
            ? closeLayeredSurface
            : () => navigate(ROUTES.WORKFLOW.ROOT)
        }
        collapsed={!chatOpen}
        onCollapsedChange={(c) => setChatOpen(!c)}
        onViewWorkflowArtifacts={openWorkflowArtifacts}
        onOpenGalleryArtifact={openArtifactFromGallery}
        onGalleryChange={setSurface}
        onRunChange={handleRunChange}
        warnOnUnsavedChanges={isAnchorSurfaceVisible}
        renderRailHeader={renderRailHeader}
      />
    </AgentChatProvider>
  );
}
